import React, { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/hooks/useLanguage";
import { supabase } from "@/integrations/supabase/client";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Check, X, Crown, Loader2 } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useQuery } from "@tanstack/react-query";
import { logger } from "@/lib/logger";

const SubscriptionComparison = () => {
  const { t } = useLanguage();
  const { toast } = useToast();
  const [selectedBillingCycle, setSelectedBillingCycle] = useState<'monthly' | 'yearly'>('monthly');
  const [isLoading, setIsLoading] = useState(false);

  const { data: currentSubscription } = useQuery({
    queryKey: ['subscription'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', user.id)
        .eq('status', 'active')
        .single();

      if (error) {
        logger.error('Subscription fetch error:', error);
        return null;
      }
      return data;
    }
  });

  const subscriptionFeatures = [
    {
      name: t('settings.subscription.planComparison.featureItems.dailyStoryLimit'),
      free: `${1}${t('settings.subscription.planComparison.limits.perDay')}`,
      basic: `${5}${t('settings.subscription.planComparison.limits.perDay')}`,
      premium: t('settings.subscription.planComparison.limits.unlimited'),
      type: "text"
    },
    {
      name: t('settings.subscription.planComparison.featureItems.aiStoryGeneration'),
      free: true,
      basic: true,
      premium: true,
      type: "boolean"
    },
    {
      name: t('settings.subscription.planComparison.featureItems.customResponses'),
      free: true,
      basic: true,
      premium: true,
      type: "boolean"
    },
    {
      name: t('settings.subscription.planComparison.featureItems.aiTitleSuggestion'),
      free: false,
      basic: true,
      premium: true,
      type: "boolean"
    },
    {
      name: t('settings.subscription.planComparison.featureItems.customStorySelection'),
      free: false,
      basic: true,
      premium: true,
      type: "boolean"
    },
    {
      name: t('settings.subscription.planComparison.featureItems.publicSharing'),
      free: false,
      basic: true,
      premium: true,
      type: "boolean"
    },
    {
      name: t('settings.subscription.planComparison.featureItems.characterManagement'),
      free: false,
      basic: true,
      premium: true,
      type: "boolean"
    },
    {
      name: t('settings.subscription.planComparison.featureItems.aiProviderSelection'),
      free: false,
      basic: true,
      premium: true,
      type: "boolean"
    },
    {
      name: t('settings.subscription.planComparison.featureItems.statsAndBadges'),
      free: false,
      basic: true,
      premium: true,
      type: "boolean"
    },
    {
      name: t('settings.subscription.planComparison.featureItems.badgeRewards'),
      free: false,
      basic: true,
      premium: true,
      type: "boolean"
    },
    {
      name: t('settings.subscription.planComparison.featureItems.naturalStoryLength'),
      free: false,
      basic: false,
      premium: true,
      type: "boolean"
    },
    {
      name: t('settings.subscription.planComparison.featureItems.sequelsAndExtract'),
      free: false,
      basic: false,
      premium: true,
      type: "boolean"
    },
    {
      name: t('settings.subscription.planComparison.featureItems.characterAvatar'),
      free: false,
      basic: false,
      premium: true,
      type: "boolean"
    },
    {
      name: t('settings.subscription.planComparison.featureItems.earlyAccessFeatures'),
      free: false,
      basic: false,
      premium: true,
      type: "boolean"
    }
  ];

  const getFormattedPrice = (amount: string, cycle: 'monthly' | 'yearly') => {
    const numAmount = amount.replace(/[^0-9]/g, '');
    const unit = cycle === 'monthly' 
      ? t('settings.subscription.priceUnits.month') 
      : t('settings.subscription.priceUnits.year');
    
    return `${numAmount}€/${unit}`;
  };
  
  const prices = {
    monthly: {
      basic: getFormattedPrice("4€", 'monthly'),
      premium: getFormattedPrice("6€", 'monthly')
    },
    yearly: {
      basic: getFormattedPrice("40€", 'yearly'),
      premium: getFormattedPrice("60€", 'yearly')
    }
  };

  const stripePriceIds = {
    monthly: {
      basic: 'price_1RIxu4GwsHJnV0daxNbtbo5H',
      premium: 'price_1RIxs2GwsHJnV0darciXN9Mp'
    },
    yearly: {
      basic: 'price_1RIxu4GwsHJnV0dazLEafaIs',
      premium: 'price_1RIxs2GwsHJnV0da8384vtqw'
    }
  };

  const handleUpgrade = async (tier: 'basic' | 'premium') => {
    try {
      setIsLoading(true);
      
      const priceId = stripePriceIds[selectedBillingCycle][tier];
      
      // Check if user has an existing subscription - if so, update it instead of creating a new one
      if (currentSubscription?.stripe_subscription_id) {
        // User is upgrading an existing subscription - use update-subscription
        const { data, error } = await supabase.functions.invoke('update-subscription', {
          body: {
            subscriptionId: currentSubscription.stripe_subscription_id,
            newPriceId: priceId,
          },
        });
        
        if (error) {
          throw new Error(error.message);
        }
        
        // If there's a checkout URL for confirming payment, redirect to it
        if (data?.checkout_url) {
          window.location.href = data.checkout_url;
          return; // Return early since we're redirecting
        }
        
        // If no redirect URL, show a success message
        toast({
          title: "Subscription Updated",
          description: `You have been upgraded to the ${tier} tier`,
        });
        
        // Refresh the page to update the UI
        window.location.reload();
      } else {
        // User doesn't have a subscription yet - create a new one with checkout
        const { data: sessionData, error: sessionError } = await supabase.functions.invoke(
          'create-checkout-session',
          {
            body: {
              priceId,
              successUrl: `${window.location.origin}/dashboard?subscription=success`,
              cancelUrl: `${window.location.origin}/dashboard?subscription=canceled`,
            },
          }
        );

        if (sessionError) {
          throw new Error(sessionError.message);
        }

        if (sessionData?.url) {
          window.location.href = sessionData.url;
        }
      }
    } catch (error) {
      logger.error('Error updating subscription:', error);
      toast({
        title: "Error",
        description: "Failed to update subscription. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDowngrade = async (targetTier: 'free' | 'basic') => {
    try {
      setIsLoading(true);
      
      if (!currentSubscription?.stripe_subscription_id) {
        throw new Error('No active subscription found');
      }

      if (targetTier === 'free') {
        // If downgrading to free, cancel the subscription in Stripe
        await supabase.functions.invoke('cancel-subscription', {
          body: { subscriptionId: currentSubscription.stripe_subscription_id }
        });
        
        toast({
          title: "Subscription Canceled",
          description: "You have been downgraded to the free tier",
        });
        
        // Refresh the page to update the UI (only for free downgrade)
        window.location.reload();
      } else if (targetTier === 'basic') {
        // For downgrading to basic, use the update-subscription edge function
        // to modify the existing subscription instead of cancel and resubscribe
        const priceId = stripePriceIds[selectedBillingCycle].basic;
        
        const { data, error } = await supabase.functions.invoke('update-subscription', {
          body: {
            subscriptionId: currentSubscription.stripe_subscription_id,
            newPriceId: priceId,
          },
        });
        
        if (error) {
          throw new Error(error.message);
        }
        
        toast({
          title: "Subscription Updated",
          description: "You have been downgraded to the basic tier",
        });
        
        // Refresh the page to update the UI
        window.location.reload();
      }
    } catch (error) {
      logger.error('Error downgrading subscription:', error);
      toast({
        title: "Error",
        description: "Failed to downgrade subscription. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const renderSubscriptionButtons = () => {
    if (isLoading) {
      return (
        <Button disabled className="w-full">
          <Loader2 className="h-4 w-4 animate-spin mr-2" />
          {t('settings.subscription.planComparison.processing')}
        </Button>
      );
    }

    switch (currentSubscription?.tier) {
      case 'premium':
        return (
          <div className="flex flex-col gap-4">
            <Button
              onClick={() => handleDowngrade('basic')}
              variant="outline"
              className="w-full"
            >
              {t('settings.subscription.planComparison.downgradeToBasic')}
            </Button>
            <Button
              onClick={() => handleDowngrade('free')}
              variant="outline"
              className="w-full"
            >
              {t('settings.subscription.planComparison.downgradeToFree')}
            </Button>
          </div>
        );
      case 'basic':
        return (
          <div className="flex flex-col gap-4">
            <Button
              onClick={() => handleUpgrade('premium')}
              className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
            >
              {t('settings.subscription.planComparison.upgradeToPremium')}
            </Button>
            <Button
              onClick={() => handleDowngrade('free')}
              variant="outline"
              className="w-full"
            >
              {t('settings.subscription.planComparison.downgradeToFree')}
            </Button>
          </div>
        );
      default: // free or no subscription
        return (
          <div className="flex flex-col gap-4">
            <Button
              onClick={() => handleUpgrade('basic')}
              className="w-full bg-purple-600 hover:bg-purple-700"
            >
              {t('settings.subscription.planComparison.upgradeToBasic')}
            </Button>
            <Button
              onClick={() => handleUpgrade('premium')}
              className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
            >
              {t('settings.subscription.planComparison.upgradeToPremium')}
            </Button>
          </div>
        );
    }
  };

  // Format date to a user-friendly string
  const formatDate = (dateString: string | null) => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    return new Intl.DateTimeFormat(navigator.language, { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    }).format(date);
  };

  return (
    <Card className="p-2 sm:p-6 py-4 bg-white/5 backdrop-blur-sm border border-white/10">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <Crown className="h-6 w-6 text-white" />
              <h2 className="text-2xl font-bold text-white">
                {t('settings.subscription.title')}
              </h2>
            </div>
            <p className="text-white/60">
              {t('settings.subscription.planComparison.chooseYourPlan')}
            </p>
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2 bg-white/10 rounded-lg p-2">
            <Crown className="h-5 w-5 text-yellow-500" />
            <div className="flex flex-col">
              <span className="font-bold text-white/80 text-sm">
                {t('settings.subscription.planComparison.currentPlanLabel')}{' '}
                {currentSubscription?.tier 
                  ? t(`settings.subscription.tiers.${currentSubscription.tier}.name`)
                  : t('settings.subscription.tiers.free.name')}
              </span>
              
              {/* Show scheduled downgrade information if applicable */}
              {currentSubscription?.scheduled_downgrade_tier && (
                <span className="text-xs text-amber-400">
                  {t('settings.subscription.planComparison.downgradingTo')}{' '}
                  {t(`settings.subscription.tiers.${currentSubscription.scheduled_downgrade_tier}.name`)}{' '}
                  {t('settings.subscription.planComparison.effectiveOn')}{' '}
                  {formatDate(currentSubscription.current_period_end)}
                </span>
              )}
              
              {/* Show next billing date for paid plans */}
              {currentSubscription?.tier !== 'free' && currentSubscription?.current_period_end && (
                <span className="text-xs text-white/60">
                  {t('settings.subscription.planComparison.nextBillingDate')}: {formatDate(currentSubscription.current_period_end)}
                </span>
              )}
            </div>
          </div>
        </div>

        <Tabs defaultValue="monthly" className="w-full">
          <TabsList className="grid w-full grid-cols-2 max-w-[400px] mb-6 bg-white/5">
            <TabsTrigger
              className="data-[state=active]:bg-white/10"
              value="monthly"
              onClick={() => setSelectedBillingCycle('monthly')}
            >
              {t('settings.subscription.monthlyLabel')}
            </TabsTrigger>
            <TabsTrigger 
              className="data-[state=active]:bg-white/10"
              value="yearly"
              onClick={() => setSelectedBillingCycle('yearly')}
            >
              {t('settings.subscription.yearlyLabel')}
              <span className="ml-2 text-xs text-purple-400">{t('settings.subscription.planComparison.savePercentage')}</span>
            </TabsTrigger>
          </TabsList>

          <Table>
            <TableHeader>
              <TableRow className="border-white/10">
                <TableHead className="font-bold text-white">{t('settings.subscription.planComparison.features')}</TableHead>
                <TableHead className="font-bold text-white text-center w-1/5">{t('settings.subscription.tiers.free.name')}</TableHead>
                <TableHead className="font-bold text-white text-center w-1/5">{t('settings.subscription.planComparison.talecraftBasic')}</TableHead>
                <TableHead className="font-bold text-white text-center w-1/5">{t('settings.subscription.planComparison.talecraftPremium')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow className="border-white/10">
                <TableCell className="font-semibold text-white">{t('settings.subscription.planComparison.price')}</TableCell>
                <TableCell className="font-semibold text-purple-400 text-center">{t('settings.subscription.tiers.free.price')}</TableCell>
                <TableCell className="font-semibold text-purple-400 text-center">
                  {selectedBillingCycle === 'monthly' ? prices.monthly.basic : prices.yearly.basic}
                </TableCell>
                <TableCell className="font-semibold text-purple-400 text-center">
                  {selectedBillingCycle === 'monthly' ? prices.monthly.premium : prices.yearly.premium}
                </TableCell>
              </TableRow>
              {subscriptionFeatures.map((feature, index) => (
                <TableRow key={index} className="border-white/10">
                  <TableCell className="font-semibold text-white">{feature.name}</TableCell>
                  <TableCell className="text-center">
                    {feature.type === "text" ? (
                      <span className="text-white">{feature.free}</span>
                    ) : (
                      feature.free ? (
                        <div className="flex justify-center">
                          <Check className="h-4 w-4 text-green-500" strokeWidth={4} />
                        </div>
                      ) : (
                        <div className="flex justify-center">
                          <X className="h-4 w-4 text-red-500" strokeWidth={4} />
                        </div>
                      )
                    )}
                  </TableCell>
                  <TableCell className="text-center">
                    {feature.type === "text" ? (
                      <span className="text-white">{feature.basic}</span>
                    ) : (
                      feature.basic ? (
                        <div className="flex justify-center">
                          <Check className="h-4 w-4 text-green-500" strokeWidth={4} />
                        </div>
                      ) : (
                        <div className="flex justify-center">
                          <X className="h-4 w-4 text-red-500" strokeWidth={4} />
                        </div>
                      )
                    )}
                  </TableCell>
                  <TableCell className="text-center">
                    {feature.type === "text" ? (
                      <span className="text-white">{feature.premium}</span>
                    ) : (
                      <div className="flex justify-center">
                        <Check className="h-4 w-4 text-green-500" strokeWidth={4} />
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <div className="flex justify-center">
            {renderSubscriptionButtons()}
          </div>
        </Tabs>
      </div>
    </Card>
  );
};

export default SubscriptionComparison;
